"use client"

/**
 * PriceSparkline — the interactive PDP price history (FRIDGE-282).
 *
 * Hand-rolled inline SVG on purpose. recharts is in the dependency list and is
 * the right tool on the /data pages, but the PDP already exceeds its HTML
 * budget on reviewed products (FRIDGE-255) and desktop first paint fails on
 * several families (FRIDGE-311). A charting library in the money zone would be
 * paid for on every product page to draw at most 90 points. This draws them in
 * a few hundred bytes of markup and one piece of state.
 *
 * THE HONESTY RULES THIS COMPONENT KEEPS:
 *
 *  • It renders the 'chart' rung and, since FRIDGE-1151, the 'no-movement'
 *    rung, which draws flat: a price that held across every reading is a true
 *    line. The caller has already been through history-ladder.ts, so a series
 *    too thin to draw does not reach here — it gets a sentence instead.
 *
 *  • EVERY REAL OBSERVATION IS MARKED. This is the substantive change. The old
 *    version drew a continuous stroke plus a filled area through points that
 *    average roughly one reading every 10 days, and its own doc comment claimed
 *    "no interpolation into gaps" while the stroke between two points 18 days
 *    apart asserted exactly such an interpolation. Showing the observations
 *    makes the sampling density visible, so the connecting line reads as what
 *    it is — a guide between readings, not a claim about the days between them.
 *
 *  • Nothing is extended to the frame edge, no endpoint is synthesised, and the
 *    y-domain is taken from real values only.
 *
 * Uniform scaling (no preserveAspectRatio="none"): the previous version
 * stretched the viewBox to the container width, which turned the two marker
 * circles into ellipses and misrepresented the slope of every segment.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { SparkPoint } from "./derive"

interface PriceSparklineProps {
  points: SparkPoint[]
  /**
   * Live displayed price and all-time tracked low. Accepted for the caller's
   * convenience but deliberately NOT part of the y-domain: neither is drawn
   * (the trailing marker is the latest READING, the green dot the lowest
   * READING in view), and an undrawn value in the domain only adds invisible
   * headroom. FRIDGE-1151, measured on production 09/27/2026: a flat $1,207
   * series under a $1,349 buy-box price was pushed onto the chart floor.
   */
  currentCents?: number
  lowestCents?: number
  /** Tracked average, drawn as a reference line when known. */
  averageCents?: number | null
}

/**
 * FRIDGE-667 (owner arrows into the side margins: "should also be end to
 * end"): the old fixed W=288 viewBox with height={96} and width="100%"
 * letterboxed under the default preserveAspectRatio — the browser scaled the
 * drawing to the fixed height and CENTERED it, leaving dead margins both
 * sides on any container wider than 288px, which on the PDP card was most of
 * the card. The width is now MEASURED from the container (ResizeObserver;
 * SSR fallback below), so one SVG unit is one CSS pixel, the first reading
 * sits at the left content edge, the latest at the right, and the uniform-
 * scaling honesty rule survives: circles stay circles, slopes stay true.
 */
const SSR_W = 640
const H = 120
const PAD_X = 8
const PAD_Y = 14

/**
 * Above this many readings the observation dots stop being individually
 * legible — at the 90-point cap they sit ~3px apart and read as a dotted line —
 * so they are dropped rather than paid for in markup. See the note at the
 * render site.
 */
const DOT_LIMIT = 30

function money(cents: number): string {
  return `$${(cents / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}`
}

function longDate(iso: string): string {
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return iso
  return new Date(t).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  })
}

export function PriceSparkline({
  points,
  averageCents,
}: PriceSparklineProps) {
  const [active, setActive] = useState<number | null>(null)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const [W, setW] = useState(SSR_W)

  useEffect(() => {
    const el = wrapRef.current
    if (!el || typeof ResizeObserver === "undefined") return
    const ro = new ResizeObserver((entries) => {
      const w = Math.round(entries[0]?.contentRect?.width ?? 0)
      if (w >= 160) setW(w)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const n = points.length

  const geom = useMemo(() => {
    if (n < 2) return null
    const values = points.map((p) => p.c)
    // The average reference line joins the y-domain when present, so it can
    // never be clipped off-canvas (it used to float unlabeled and sometimes
    // outside the drawn range).
    // The y-domain is what is DRAWN: the readings, plus the average reference
    // line when there is one. Nothing undrawn widens it (see the props note).
    const min = Math.min(...values, ...(typeof averageCents === "number" && averageCents > 0 ? [averageCents] : []))
    const max = Math.max(...values, ...(typeof averageCents === "number" && averageCents > 0 ? [averageCents] : []))
    const span = max - min
    const xAt = (i: number) => PAD_X + (i / (n - 1)) * (W - PAD_X * 2)
    // FRIDGE-1151: a price that never moved has no range to scale, so the line
    // sits at mid-height rather than on the floor.
    const yAt = (c: number) => (span === 0 ? H / 2 : PAD_Y + (1 - (c - min) / span) * (H - PAD_Y * 2))
    // FRIDGE-1253: a recorded price HOLDS until the next check, so the stroke
    // steps — across at the old price to the next reading, then up or down —
    // the rule FRIDGE-1115 set for the 30-day chart. A slanted segment would
    // draw a price for every day between two readings that nobody observed.
    const line = points
      .map((p, i) => (i === 0 ? `M${xAt(i).toFixed(1)} ${yAt(p.c).toFixed(1)}` : `H${xAt(i).toFixed(1)} V${yAt(p.c).toFixed(1)}`))
      .join(" ")
    const area = `${line} L${xAt(n - 1).toFixed(1)} ${(H - PAD_Y).toFixed(1)} L${xAt(0).toFixed(1)} ${(H - PAD_Y).toFixed(1)} Z`
    let lowIdx = 0
    for (let i = 1; i < n; i++) if (points[i].c < points[lowIdx].c) lowIdx = i
    return { xAt, yAt, line, area, lowIdx }
  }, [points, n, averageCents, W])

  /**
   * Map a pointer position to the nearest real reading. Works off the rendered
   * box rather than SVG user units, so it stays correct at any container width.
   */
  const onMove = useCallback(
    (e: React.MouseEvent<SVGRectElement>) => {
      if (n < 2) return
      const box = e.currentTarget.getBoundingClientRect()
      if (box.width <= 0) return
      const frac = (e.clientX - box.left) / box.width
      // Undo the horizontal padding so the first and last readings are reachable.
      const inner = (frac * W - PAD_X) / (W - PAD_X * 2)
      const idx = Math.round(inner * (n - 1))
      setActive(Math.max(0, Math.min(n - 1, idx)))
    },
    [n, W]
  )

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        e.preventDefault()
        setActive((cur) => {
          const step = e.key === "ArrowRight" ? 1 : -1
          const next = (cur === null ? (step > 0 ? 0 : n - 1) : cur + step)
          return Math.max(0, Math.min(n - 1, next))
        })
      } else if (e.key === "Escape") {
        setActive(null)
      }
    },
    [n]
  )

  if (!geom) return null
  const { xAt, yAt, line, area, lowIdx } = geom

  const activePoint = active !== null && active >= 0 && active < n ? points[active] : null

  return (
    <div ref={wrapRef}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        role="img"
        aria-label={`Tracked price history across ${n} recorded readings, from ${longDate(points[0].d)} to ${longDate(points[n - 1].d)} | Fridge.com`}
        className="block touch-none outline-none"
        tabIndex={0}
        onKeyDown={onKeyDown}
        onMouseLeave={() => setActive(null)}
        onBlur={() => setActive(null)}
        data-testid="pi-spark-svg"
      >
        <defs>
          <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0469f0" stopOpacity="0.14" />
            <stop offset="100%" stopColor="#0469f0" stopOpacity="0" />
          </linearGradient>
        </defs>

        <path d={area} fill="url(#spark-fill)" />

        {/* Tracked-average reference, drawn only when we hold one. */}
        {typeof averageCents === "number" && averageCents > 0 && (
          <>
            <line
              x1={PAD_X}
              x2={W - PAD_X}
              y1={yAt(averageCents)}
              y2={yAt(averageCents)}
              stroke="#9ca3af"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
            {/* FRIDGE-667: the dashed reference used to float unlabeled —
                name it so it reads as the average, not a mystery ceiling. */}
            <text
              x={W - PAD_X}
              y={yAt(averageCents) - 4}
              textAnchor="end"
              fontSize={10}
              fill="#6b7280"
            >
              avg {money(averageCents)}
            </text>
          </>
        )}

        <path
          d={line}
          fill="none"
          stroke="#0469f0"
          strokeWidth={1.75}
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/*
          Mark every real observation ONLY while the readings are sparse enough
          for the marks to mean something. Below the threshold the readings sit
          months apart and the stroke between them is a guide, not a claim about
          the days in between — showing where we actually looked is the honest
          correction. Above it the readings are already denser than the pixels,
          the line is a fair picture on its own, and 90 circles would cost about
          11 KB of markup on a page type that is over its budget (FRIDGE-255).
        */}
        {n <= DOT_LIMIT &&
          points.map((p, i) => (
            <circle
              key={`${p.d}-${i}`}
              cx={xAt(i)}
              cy={yAt(p.c)}
              r={1.6}
              fill="#ffffff"
              stroke="#0469f0"
              strokeWidth={1}
            />
          ))}

        {/*
          ONE transparent overlay instead of a hit target per reading. Ninety
          rects cost roughly 18 KB of markup for behaviour that a single
          pointer-position calculation gives for free.
        */}
        <rect
          x={0}
          y={0}
          width={W}
          height={H}
          fill="transparent"
          onMouseMove={onMove}
          data-testid="pi-spark-hit"
        />

        {/* The reading being read out. */}
        {active !== null && activePoint && (
          <circle
            cx={xAt(active)}
            cy={yAt(activePoint.c)}
            r={3.2}
            fill="#0469f0"
            stroke="#fff"
            strokeWidth={1.25}
          />
        )}

        {/* Active read-out guide */}
        {active !== null && activePoint && (
          <line
            x1={xAt(active)}
            x2={xAt(active)}
            y1={PAD_Y / 2}
            y2={H - PAD_Y / 2}
            stroke="#0469f0"
            strokeWidth={1}
            strokeOpacity={0.35}
          />
        )}

        {/* lowest tracked reading */}
        <circle cx={xAt(lowIdx)} cy={yAt(points[lowIdx].c)} r={3} fill="#16a34a" stroke="#fff" strokeWidth={1.5} />
        {/* most-recent tracked reading */}
        <circle cx={xAt(n - 1)} cy={yAt(points[n - 1].c)} r={3.25} fill="#ff9951" stroke="#fff" strokeWidth={1.5} />
      </svg>

      {/* Read-out. Reserves its own line so hovering never shifts the layout. */}
      <p
        className="mt-1 min-h-[16px] text-[11px] leading-4 text-gray-700"
        aria-live="polite"
        data-testid="pi-spark-readout"
      >
        {activePoint ? (
          <>
            <span className="font-semibold text-gray-900">{money(activePoint.c)}</span>
            <span className="text-gray-500"> on {longDate(activePoint.d)}</span>
          </>
        ) : (
          <span className="text-gray-500">
            {n} readings · hover or use the arrow keys to read one
          </span>
        )}
      </p>
    </div>
  )
}
